import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"
import { ArrowLeft, CheckCircle, XCircle, DollarSign, Globe, FileText } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export default function IThemesSecurityAlternative() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>iThemes Security Alternative - Multi-Platform Scanning | Fusegu</title>
        <meta
          name="description"
          content="Looking for an iThemes Security alternative? Fusegu scans WordPress AND any web platform with comprehensive vulnerability detection. White-label reports included. Try free."
        />
        <meta property="og:title" content="iThemes Security Alternative - Scan Beyond WordPress" />
        <meta property="og:description" content="Switch from iThemes Security to Fusegu for multi-platform scanning, white-label reports, and comprehensive security testing beyond WordPress." />
        <meta property="og:url" content="https://fusegusecurity.com/ithemes-security-alternative" />
        <meta name="keywords" content="iThemes Security alternative, WordPress security, multi-platform security, vulnerability scanning, website security" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          <div className="flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-2">
              <FuseguLogo className="h-6 w-6 flex-shrink-0" />
              <span className="text-lg font-bold">Fusegu</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-4xl mx-auto">
            {/* Back to home link */}
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>

            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                iThemes Security Alternative - Scan Beyond WordPress
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Looking for an iThemes Security alternative? Fusegu offers comprehensive security scanning
                for WordPress AND any other web platform - perfect for agencies with diverse client portfolios.
              </p>
            </div>

            {/* Quick Value Props */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-2 border-blue-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Globe className="h-6 w-6 text-blue-600" />
                    <CardTitle className="text-lg">Any Platform</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">WordPress, React, Node.js, PHP, and more</p>
                  <p className="text-sm font-semibold text-blue-600">Not limited to WordPress</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-lg">Client Reports</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">White-label PDF reports</p>
                  <p className="text-sm font-semibold text-green-600">Included in base plan</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-6 w-6 text-purple-600" />
                    <CardTitle className="text-lg">Unlimited Sites</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 mb-2">50 scans per day</p>
                  <p className="text-sm font-semibold text-purple-600">$49/month flat rate</p>
                </CardContent>
              </Card>
            </div>

            {/* Why Teams Switch */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Why Teams Switch from iThemes Security to Fusegu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Scan Beyond WordPress</p>
                    <p className="text-gray-600">iThemes only works with WordPress. Fusegu scans any web application - React, Node.js, custom platforms, and more.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">External Scanning (No Plugin Needed)</p>
                    <p className="text-gray-600">Fusegu scans without installing plugins on client sites. Save setup time and avoid plugin conflicts.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Professional Client Reports</p>
                    <p className="text-gray-600">Generate white-labeled PDF reports with your branding. iThemes doesn't offer this - it's designed for site owners, not agencies.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Broader Security Testing</p>
                    <p className="text-gray-600">Get OWASP Top 10, SSL/TLS analysis, infrastructure checks, and more - not just WordPress-specific security.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Centralized Dashboard</p>
                    <p className="text-gray-600">Manage all client sites from one dashboard instead of logging into each WordPress admin panel.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">iThemes Security vs Fusegu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Feature</TableHead>
                        <TableHead>iThemes Security Pro</TableHead>
                        <TableHead>Fusegu</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Platform Support</TableCell>
                        <TableCell>WordPress only</TableCell>
                        <TableCell className="font-bold text-green-600">Any web application</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Price (Solid Suite)</TableCell>
                        <TableCell>$199/year for 1 site</TableCell>
                        <TableCell className="font-bold text-green-600">$588/year unlimited sites</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Installation</TableCell>
                        <TableCell>Plugin (must install on each site)</TableCell>
                        <TableCell className="font-bold text-green-600">No installation needed</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">White-label Reports</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Not available</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Included</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Centralized Dashboard</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Site by site management</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Security Scanners</TableCell>
                        <TableCell>WordPress-focused checks</TableCell>
                        <TableCell className="font-bold">10+ tools (OWASP ZAP, Nuclei, SSL Labs, etc.)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SSL/TLS Scanning</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes (SSL Labs)</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SOC2 Compliance</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Two-Factor Auth</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Scanning only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">File Change Detection</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Brute Force Protection</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* When iThemes is Better */}
            <Card className="mb-8 border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <span>When iThemes Security is Better</span>
                  <Badge variant="outline" className="ml-2">Honest Comparison</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">iThemes Security is excellent for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Active WordPress protection</strong> - Two-factor auth, brute force protection, file monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>WordPress-only sites</strong> - If you exclusively manage WordPress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Real-time security hardening</strong> - Blocks attacks as they happen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Lower annual cost for single sites</strong> - $99/year vs $588/year for Fusegu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Site owners who self-manage</strong> - Designed for end users, not agencies</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* When Fusegu is Better */}
            <Card className="mb-8 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-600">When Fusegu is Better</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Fusegu is perfect for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Agencies with diverse client platforms</strong> - WordPress, React, Node.js, custom apps all in one tool</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Managing multiple sites</strong> - After 6 sites, Fusegu becomes cheaper than iThemes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Client reporting needs</strong> - White-labeled PDFs with your branding for professional presentations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>External security audits</strong> - No plugin installation, scan any publicly accessible site</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Comprehensive vulnerability scanning</strong> - OWASP Top 10, SSL/TLS, infrastructure, and more</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Compliance requirements</strong> - SOC2 compliance mapping included</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pricing Comparison */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl">Pricing Comparison by Agency Size</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 5 Client Sites:</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">SolidWP Solid Suite</p>
                        <p className="text-3xl font-bold">$995/year</p>
                        <p className="text-sm text-gray-600">5 sites × $199/year each</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $407/year</p>
                      <p className="text-gray-700">Plus Fusegu includes white-label reports and scans non-WordPress platforms too.</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 10 Client Sites:</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">SolidWP Solid Suite</p>
                        <p className="text-3xl font-bold">$1,990/year</p>
                        <p className="text-sm text-gray-600">10 sites × $199/year each</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $1,402/year</p>
                      <p className="text-gray-700">The savings grow significantly as your agency scales.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu replace iThemes Security for WordPress sites?</p>
                  <p className="text-gray-700">
                    For vulnerability scanning and detection, yes. However, iThemes provides active protection
                    (two-factor auth, brute force blocking, file monitoring) that Fusegu doesn't. Many agencies
                    use Fusegu for comprehensive scanning and reporting, while keeping iThemes free version for
                    basic WordPress hardening.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Does Fusegu require installing a plugin?</p>
                  <p className="text-gray-700">
                    No! Fusegu scans externally without any installation. This is a huge advantage - you can scan
                    any site (WordPress, React, custom apps) without touching their codebase. Save hours of setup time.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">How many sites before Fusegu becomes cheaper than SolidWP?</p>
                  <p className="text-gray-700">
                    SolidWP Solid Suite: $199/year for 1 site. Fusegu: $588/year unlimited. Fusegu becomes cheaper after 3 sites
                    and includes features SolidWP doesn't have (white-label reports, multi-platform support, external scanning).
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu scan non-WordPress sites?</p>
                  <p className="text-gray-700">
                    Yes! This is a major advantage. Fusegu scans any web application - React, Vue, Node.js, PHP,
                    custom platforms. Perfect for agencies with diverse client portfolios beyond just WordPress.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">What about the white-label reports?</p>
                  <p className="text-gray-700">
                    Upload your agency logo and customize colors in the Branding section. All PDF reports automatically
                    include your branding. This feature alone is worth the price for agencies doing client reporting.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can I use both iThemes and Fusegu together?</p>
                  <p className="text-gray-700">
                    Absolutely! Many agencies use iThemes (free or paid) for WordPress-specific hardening and Fusegu
                    for comprehensive scanning and client reporting. They complement each other well.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="mb-8 bg-gradient-to-br from-blue-600 to-purple-700 text-white border-none">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Try Fusegu Free - No Credit Card Required</h2>
                <p className="text-xl mb-6 text-blue-50">
                  Scan WordPress and beyond. See the difference in 5 minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8">
                      Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 font-semibold px-8 border-2 border-white">
                      View Demo
                    </Button>
                  </Link>
                </div>
                <p className="text-sm mt-4 text-blue-100">
                  Join agencies scanning beyond WordPress
                </p>
              </CardContent>
            </Card>

          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4 md:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
            <p className="mb-4">Fusegu Security | fusegusecurity.com</p>
            <div className="flex justify-center gap-6 flex-wrap">
              <Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600">Terms of Service</Link>
              <Link to="/blogs" className="hover:text-blue-600">Blog</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
